/**
 * Roof interview + live scope preview (PRP-ROOF-2 S2).
 *
 * Measurement entry — manual fields or an uploaded EagleView XML report
 * (PRP-ROOF-1 parser) — + the
 * server-driven roof interview, with a debounced live preview of the
 * generated line items. Stateless against the backend: every keystroke that
 * settles re-posts measurements + answers-so-far to
 * `/roof/interview/preview` and re-renders the returned questions and scope.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../lib/utils';
import { BackendAPI } from '../../../lib/api';
import type {
  ApiError,
} from '../../../types';
import type {
  RoofInterviewPreviewRequest,
  RoofMeasurements,
  RoofMeasurementsParseResponse,
  RoofPitchArea,
  RoofPreviewResponse,
  RoofQuestion,
  RoofScopeResult,
} from '../../../lib/api';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';

/** Damage types for which a roof interview is meaningful (founder ruling
 * B-736: storm and fire count). Flood/water perils never surface it. Exported so the
 * builder page (and tests) share one rule. */
export function isRoofPeril(damageType: string | null | undefined): boolean {
  return ['hail', 'wind', 'hurricane', 'storm', 'fire'].includes((damageType || '').toLowerCase());
}

/** Pitch options 0/12 … 12/12 for the predominant-pitch select. */
const PITCH_OPTIONS = Array.from({ length: 13 }, (_, i) => `${i}/12`);

/** Human-readable vendor name for the parse response's `source` field
 * (PRP-ROOF-3: EagleView, Hover, Roofr). */
function vendorLabel(source: string): string {
  const labels: Record<string, string> = {
    eagleview: 'EagleView',
    hover: 'Hover',
    roofr: 'Roofr',
    gaf_quickmeasure: 'GAF QuickMeasure',
    aerialreports: 'AerialReports',
    manual: 'Manual',
    other: 'Roof',
  };
  return labels[source] || 'Roof';
}

/** The five linear-foot fields we collect manually; the rest default 0. */
const LF_FIELDS: { key: 'ridge_lf' | 'hip_lf' | 'valley_lf' | 'rake_lf' | 'eave_lf'; label: string }[] = [
  { key: 'ridge_lf', label: 'Ridge' },
  { key: 'hip_lf', label: 'Hip' },
  { key: 'valley_lf', label: 'Valley' },
  { key: 'rake_lf', label: 'Rake' },
  { key: 'eave_lf', label: 'Eave' },
];

/** A single extra pitch row in the advanced section. */
interface ExtraPitchRow {
  pitch: string;
  area: string;
}

/** Small labeled-field wrapper (mirrors V2ScopeBuilderPage's local Field). */
function Field({
  label,
  children,
  className,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-[6px]', className)}>
      <label className="text-[11.5px] font-[550] text-[var(--tx-mid)]">{label}</label>
      {children}
    </div>
  );
}

/** Small helper/hint text under a field (mirrors V2ScopeBuilderPage's Hint). */
function Hint({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] text-[var(--tx-faint)]">{children}</div>;
}

/** Uppercase mini section header. */
function MiniHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold tracking-[.06em] uppercase text-[var(--tx-faint)]">
      {children}
    </div>
  );
}

const num = (s: string): number => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

/** The persisted roof-scope blob (schema v1) merged into `scope_data.metadata.roof`.
 * The ESX exporter reads this to emit the Roof folder. */
export interface RoofScopeBlob extends Record<string, unknown> {
  schema_version: 1;
  profile_id: string;
  measurements: RoofMeasurements;
  answers: Record<string, unknown>;
  system: Record<string, unknown> | null;
  result: RoofScopeResult;
}

/**
 * Merge a roof-scope blob into a scope's `metadata.roof`, non-destructively.
 * Returns the scope unchanged (reference-equal) when `roof` is null, so callers
 * can pipe every save through it. Pure — unit-testable without mounting.
 */
export function mergeRoofIntoScope<T extends { metadata?: Record<string, unknown> | null }>(
  scope: T,
  roof: Record<string, unknown> | null,
): T {
  if (roof === null) return scope;
  return { ...scope, metadata: { ...(scope.metadata ?? {}), roof } };
}

export interface RoofInterviewPanelProps {
  /** ZIP for jurisdiction-profile resolution; passed through to the preview. */
  zip: string;
  /**
   * Fired with the roof-scope blob whenever a preview succeeds WITH a result,
   * and with `null` when the preview errors, measurements go invalid, or the
   * component unmounts (so switching damage type away clears the pending scope).
   */
  onRoofScope?: (_roof: Record<string, unknown> | null) => void;
}

/** Manual roof measurements + interview + live scope preview. */
export default function RoofInterviewPanel({ zip, onRoofScope }: RoofInterviewPanelProps) {
  // --- Measurements (manual) ---
  const [totalArea, setTotalArea] = useState('');
  const [pitch, setPitch] = useState('6/12');
  const [stories, setStories] = useState('1');
  const [lf, setLf] = useState<Record<string, string>>({});
  const [wastePct, setWastePct] = useState('');
  const [extraRows, setExtraRows] = useState<ExtraPitchRow[]>([]);

  // --- Measurements (imported report — replaces manual entry while set) ---
  const [imported, setImported] = useState<RoofMeasurementsParseResponse | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleReportUpload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const parsed = await BackendAPI.roofParseMeasurements(file);
      setImported(parsed);
    } catch (e) {
      const err = e as ApiError;
      setUploadError(err?.message || 'Could not parse the measurement report.');
    } finally {
      setUploading(false);
      // Allow re-selecting the same file after a failure or removal.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // --- Interview answers (only user-touched; server fills defaults) ---
  const [answers, setAnswers] = useState<Record<string, unknown>>({});

  // --- Preview state ---
  const [preview, setPreview] = useState<RoofPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const setAnswer = (id: string, value: unknown) =>
    setAnswers((a) => ({ ...a, [id]: value }));

  // Stable ref to the latest callback so the preview effect (keyed on payloadKey)
  // and the unmount cleanup can fire it without re-subscribing on every render.
  const onRoofScopeRef = useRef(onRoofScope);
  onRoofScopeRef.current = onRoofScope;

  // Clear any pending roof scope when the panel unmounts (e.g. the user switches
  // damage type away from a roof peril).
  useEffect(() => () => onRoofScopeRef.current?.(null), []);

  /** Assemble the request payload, or null when total area isn't positive.
   * An imported report wins over manual entry while present. */
  const payload = useMemo<RoofInterviewPreviewRequest | null>(() => {
    if (imported) {
      return {
        measurements: imported.measurements,
        answers,
        ...(zip ? { zip_code: zip } : {}),
      };
    }
    const total = num(totalArea);
    if (!(total > 0)) return null;

    // Extra pitch rows first; the predominant row absorbs the remainder so the
    // 2%-sum validation always passes.
    const extras: RoofPitchArea[] = extraRows
      .filter((r) => r.pitch && num(r.area) > 0)
      .map((r) => ({ pitch: r.pitch, area_sf: num(r.area) }));
    const extraSum = extras.reduce((s, r) => s + r.area_sf, 0);
    const predominantArea = Math.max(0, total - extraSum);
    const areas_by_pitch: RoofPitchArea[] = [
      { pitch, area_sf: predominantArea },
      ...extras,
    ];

    const measurements: RoofMeasurements = {
      source: 'manual',
      total_area_sf: total,
      predominant_pitch: pitch,
      stories: Math.max(1, Math.round(num(stories) || 1)),
      areas_by_pitch,
      lengths: {
        ridge_lf: num(lf.ridge_lf || ''),
        hip_lf: num(lf.hip_lf || ''),
        valley_lf: num(lf.valley_lf || ''),
        rake_lf: num(lf.rake_lf || ''),
        eave_lf: num(lf.eave_lf || ''),
        drip_edge_lf: 0,
        flashing_lf: 0,
        step_flashing_lf: 0,
        parapet_lf: 0,
      },
      ...(wastePct.trim() ? { waste: { suggested_pct: num(wastePct) } } : {}),
    };

    return {
      measurements,
      answers,
      ...(zip ? { zip_code: zip } : {}),
    };
  }, [imported, totalArea, pitch, stories, lf, wastePct, extraRows, answers, zip]);

  const payloadKey = payload ? JSON.stringify(payload) : '';

  // Debounced preview. Keeps the previous result rendered until the new one
  // arrives; only fires when total area > 0 (payload non-null).
  useEffect(() => {
    if (!payload) {
      setPreview(null);
      // Measurements are invalid — no scope to persist.
      onRoofScopeRef.current?.(null);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await BackendAPI.roofInterviewPreview(payload);
        setValidationError(null);
        setPreview(res);
        // Persist the roof scope only on a clean result; clear it otherwise so a
        // stale scope never survives an error or an unsupported system.
        if (res.result && !res.error) {
          const blob: RoofScopeBlob = {
            schema_version: 1,
            profile_id: res.profile_id,
            measurements: payload.measurements,
            answers,
            system: res.system ?? null,
            result: res.result,
          };
          onRoofScopeRef.current?.(blob);
        } else {
          onRoofScopeRef.current?.(null);
        }
        // Reset answers whose question is no longer visible (e.g. selected_facets
        // once damage_mode leaves facet_selection).
        setAnswers((prev) => {
          const visible = new Set(res.questions.map((q) => q.id));
          let changed = false;
          const next: Record<string, unknown> = {};
          for (const k of Object.keys(prev)) {
            if (visible.has(k)) next[k] = prev[k];
            else changed = true;
          }
          return changed ? next : prev;
        });
      } catch (e) {
        const err = e as ApiError;
        setValidationError(err?.message || 'Roof preview failed.');
        onRoofScopeRef.current?.(null);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey]);

  const questions = preview?.questions ?? [];
  const effective = preview?.effective_answers ?? {};

  /** Current value for a question: local answer wins, else server effective. */
  const valueOf = (q: RoofQuestion): unknown =>
    q.id in answers ? answers[q.id] : effective[q.id];

  return (
    <div className="grid gap-[18px] md:grid-cols-2" data-testid="roof-interview-panel">
      {/* ---- Left: measurements + interview ---- */}
      <div className="flex flex-col gap-[16px]">
        <div className="flex flex-col gap-[12px]">
          <div className="flex items-center justify-between">
            <MiniHeader>Roof measurements</MiniHeader>
            {!imported && (
              <button
                type="button"
                data-testid="roof-upload-report"
                className="text-[12px] font-[530] text-[var(--accent-ink)] hover:underline disabled:opacity-50"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading
                  ? 'Parsing report…'
                  : 'Upload roof report (EagleView, Hover, Roofr)'}
              </button>
            )}
            <input
              ref={fileInputRef}
              data-testid="roof-report-file"
              type="file"
              accept=".xml,.pdf,text/xml,application/xml,application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleReportUpload(file);
              }}
            />
          </div>

          {uploadError && (
            <div
              data-testid="roof-upload-error"
              className="text-[12px] text-[var(--bad)] bg-[var(--bad-bg)] border border-[color-mix(in_oklab,var(--bad)_26%,transparent)] rounded-[var(--r-md)] px-[10px] py-[8px]"
            >
              {uploadError}
            </div>
          )}

          {imported && (
            <div
              data-testid="roof-imported-card"
              className="flex flex-col gap-[6px] rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-[12px] py-[10px]"
            >
              <div className="flex items-center justify-between gap-[8px]">
                <div className="text-[12.5px] font-[600] text-[var(--tx)]">
                  {vendorLabel(imported.source)} report{' '}
                  {imported.measurements.report_id ? `#${imported.measurements.report_id}` : ''}
                </div>
                <button
                  type="button"
                  data-testid="roof-remove-report"
                  className="text-[12px] text-[var(--tx-mid)] hover:text-[var(--bad)]"
                  onClick={() => setImported(null)}
                >
                  Remove
                </button>
              </div>
              {imported.measurements.property_address && (
                <div className="text-[11.5px] text-[var(--tx-mid)]">
                  {imported.measurements.property_address}
                </div>
              )}
              <div className="text-[11.5px] text-[var(--tx-mid)]">
                {imported.measurements.total_area_sf.toLocaleString()} SF ·{' '}
                {imported.measurements.predominant_pitch} predominant
                {imported.measurements.facet_count
                  ? ` · ${imported.measurements.facet_count} facets`
                  : ''}
                {imported.measurements.waste?.suggested_pct != null
                  ? ` · ${imported.measurements.waste.suggested_pct}% suggested waste`
                  : ''}
              </div>
              {imported.dialect === 'lean' && (
                <Hint>
                  Older report format: edge lengths were derived from the report's roof
                  geometry.
                </Hint>
              )}
            </div>
          )}

          {!imported && (
          <>
          <div className="grid gap-[12px] grid-cols-2">
            <Field label="Total roof area (SF)">
              <Input
                data-testid="roof-total-area"
                inputMode="decimal"
                placeholder="e.g. 2400"
                value={totalArea}
                onChange={(e) => setTotalArea(e.target.value)}
              />
            </Field>
            <Field label="Predominant pitch">
              <Select
                data-testid="roof-pitch"
                value={pitch}
                onChange={(e) => setPitch(e.target.value)}
              >
                {PITCH_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Stories">
              <Input
                data-testid="roof-stories"
                inputMode="numeric"
                min={1}
                type="number"
                value={stories}
                onChange={(e) => setStories(e.target.value)}
              />
            </Field>
            <Field label="Waste % (optional)">
              <Input
                data-testid="roof-waste"
                inputMode="decimal"
                placeholder="e.g. 15"
                value={wastePct}
                onChange={(e) => setWastePct(e.target.value)}
              />
            </Field>
          </div>

          <div className="grid gap-[12px] grid-cols-5">
            {LF_FIELDS.map((f) => (
              <Field key={f.key} label={f.label}>
                <Input
                  data-testid={`roof-lf-${f.key}`}
                  inputMode="decimal"
                  placeholder="LF"
                  value={lf[f.key] || ''}
                  onChange={(e) => setLf((prev) => ({ ...prev, [f.key]: e.target.value }))}
                />
              </Field>
            ))}
          </div>
          <Hint>Ridge, hip, valley, rake, and eave in linear feet. Others default to zero.</Hint>

          {/* Advanced: additional pitch rows */}
          <div className="flex flex-col gap-[8px]">
            <MiniHeader>Additional pitches (optional)</MiniHeader>
            {extraRows.map((row, i) => (
              <div key={i} className="grid gap-[8px] grid-cols-[1fr_1fr_auto] items-end">
                <Field label="Pitch">
                  <Select
                    data-testid={`roof-extra-pitch-${i}`}
                    value={row.pitch}
                    onChange={(e) =>
                      setExtraRows((rows) =>
                        rows.map((r, j) => (j === i ? { ...r, pitch: e.target.value } : r)),
                      )
                    }
                  >
                    {PITCH_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Area (SF)">
                  <Input
                    data-testid={`roof-extra-area-${i}`}
                    inputMode="decimal"
                    value={row.area}
                    onChange={(e) =>
                      setExtraRows((rows) =>
                        rows.map((r, j) => (j === i ? { ...r, area: e.target.value } : r)),
                      )
                    }
                  />
                </Field>
                <button
                  type="button"
                  className="h-[34px] px-[10px] rounded-[var(--r-md)] border border-[var(--bd)] text-[12px] text-[var(--tx-mid)] hover:text-[var(--bad)]"
                  onClick={() => setExtraRows((rows) => rows.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="roof-add-pitch"
              className="self-start text-[12px] font-[530] text-[var(--accent-ink)] hover:underline"
              onClick={() => setExtraRows((rows) => [...rows, { pitch: '4/12', area: '' }])}
            >
              + Add pitch row
            </button>
            <Hint>The predominant-pitch area auto-adjusts to the remaining square footage.</Hint>
          </div>
          </>
          )}
        </div>

        {/* ---- Interview questions ---- */}
        {questions.length > 0 && (
          <div className="flex flex-col gap-[12px]">
            <MiniHeader>Roof system</MiniHeader>
            <RoofQuestions
              questions={questions}
              valueOf={valueOf}
              setAnswer={setAnswer}
              hasFacets={!!imported?.measurements.facets?.length}
            />
          </div>
        )}
      </div>

      {/* ---- Right: live preview ---- */}
      <div className="flex flex-col gap-[10px]">
        <div className="flex items-center gap-[8px]">
          <MiniHeader>Live scope preview</MiniHeader>
          {loading && (
            <span
              data-testid="roof-preview-loading"
              className="text-[11px] text-[var(--tx-faint)]"
            >
              updating…
            </span>
          )}
        </div>

        {!payload && (
          <div className="text-[12px] text-[var(--tx-faint)]">
            Enter a total roof area to preview the scope.
          </div>
        )}

        {payload && validationError && (
          <div
            data-testid="roof-preview-validation"
            className="text-[12px] text-[var(--bad)] bg-[var(--bad-bg)] border border-[color-mix(in_oklab,var(--bad)_26%,transparent)] rounded-[var(--r-md)] px-[10px] py-[8px]"
          >
            {validationError}
          </div>
        )}

        {payload && !validationError && preview && (
          <div className="flex flex-col gap-[10px]">
            <div className="flex items-center gap-[8px] flex-wrap">
              <Badge kind="accent">{preview.profile_id}</Badge>
              {preview.result && (
                <span className="text-[11px] text-[var(--tx-faint)]">
                  {preview.result.line_items.length} line item
                  {preview.result.line_items.length === 1 ? '' : 's'}
                </span>
              )}
            </div>

            {preview.result?.warnings?.map((w, i) => (
              <div
                key={i}
                className="text-[11px] text-[var(--warn)] bg-[var(--warn-bg)] border border-[color-mix(in_oklab,var(--warn)_26%,transparent)] rounded-[var(--r-md)] px-[10px] py-[6px]"
              >
                {w}
              </div>
            ))}

            {preview.error ? (
              <div
                data-testid="roof-preview-error"
                className="text-[12px] text-[var(--tx-mid)] bg-[var(--bg-3)] border border-[var(--bd)] rounded-[var(--r-md)] px-[10px] py-[8px]"
              >
                {preview.error.message}
              </div>
            ) : (
              <ul data-testid="roof-preview-lines" className="flex flex-col gap-[6px]">
                {preview.result?.line_items.map((li, i) => (
                  <li
                    key={`${li.rule_id}-${i}`}
                    className="flex items-start gap-[8px] px-[10px] py-[7px] rounded-[var(--r-md)] bg-[var(--bg-2)] border border-[var(--bd-subtle)]"
                  >
                    <span className="font-[var(--font-mono)] text-[13px] text-[var(--tx-hi)] w-[16px] text-center shrink-0">
                      {li.activity || ''}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="flex items-center gap-[6px] flex-wrap">
                        <span className="font-[var(--font-mono)] text-[11.5px] text-[var(--tx-mid)]">
                          {li.cat} {li.sel}
                        </span>
                        <span className="text-[10px] uppercase tracking-[.04em] text-[var(--tx-faint)]">
                          {li.section === 'low_slope' ? 'low-slope' : 'steep'}
                        </span>
                        {li.review_required && <Badge kind="warn">review</Badge>}
                      </span>
                      <span className="block text-[12.5px] text-[var(--tx-hi)]">{li.dsc}</span>
                    </span>
                    <span className="font-[var(--font-mono)] text-[12px] text-[var(--tx-mid)] whitespace-nowrap shrink-0">
                      {li.quantity} {li.unit}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Renders the interview questions in order, grouping penetrations. */
function RoofQuestions({
  questions,
  valueOf,
  setAnswer,
  hasFacets,
}: {
  questions: RoofQuestion[];
  valueOf: (_q: RoofQuestion) => unknown;
  setAnswer: (_id: string, _value: unknown) => void;
  hasFacets: boolean;
}) {
  const firstPenIdx = questions.findIndex((q) => q.group === 'penetrations');
  const penQuestions = questions.filter((q) => q.group === 'penetrations');

  return (
    <div className="flex flex-col gap-[12px]">
      {questions.map((q, i) => {
        if (q.group === 'penetrations') {
          if (i !== firstPenIdx) return null;
          return (
            <div key="penetrations" className="flex flex-col gap-[10px]">
              <MiniHeader>Penetrations</MiniHeader>
              <div className="grid gap-[10px] grid-cols-2">
                {penQuestions.map((pq) => (
                  <QuestionControl
                    key={pq.id}
                    q={pq}
                    value={valueOf(pq)}
                    setAnswer={setAnswer}
                    hasFacets={hasFacets}
                  />
                ))}
              </div>
            </div>
          );
        }
        return (
          <QuestionControl
            key={q.id}
            q={q}
            value={valueOf(q)}
            setAnswer={setAnswer}
            hasFacets={hasFacets}
          />
        );
      })}
    </div>
  );
}

/** Renders one question's control by type, honoring damage_mode ui_hints. */
function QuestionControl({
  q,
  value,
  setAnswer,
  hasFacets,
}: {
  q: RoofQuestion;
  value: unknown;
  setAnswer: (_id: string, _value: unknown) => void;
  hasFacets: boolean;
}) {
  if (q.id === 'damage_mode') {
    return <DamageModeControl q={q} value={value} setAnswer={setAnswer} hasFacets={hasFacets} />;
  }

  if (q.type === 'boolean') {
    return (
      <label className="flex items-start gap-[10px] cursor-pointer" data-testid={`roof-q-${q.id}`}>
        <Checkbox
          checked={!!value}
          onCheckedChange={(v) => setAnswer(q.id, !!v)}
          className="mt-[1px]"
        />
        <span>
          <span className="text-[12.5px] font-[530] text-[var(--tx-hi)]">{q.label}</span>
          {q.help && <span className="block text-[11px] text-[var(--tx-faint)]">{q.help}</span>}
        </span>
      </label>
    );
  }

  if (q.type === 'count') {
    return (
      <Field label={q.label}>
        <Input
          data-testid={`roof-q-${q.id}`}
          type="number"
          inputMode="numeric"
          min={q.min ?? 0}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            setAnswer(q.id, Number.isFinite(n) ? Math.max(q.min ?? 0, n) : q.min ?? 0);
          }}
        />
        {q.help && <Hint>{q.help}</Hint>}
      </Field>
    );
  }

  if (q.type === 'multi_select') {
    const opts = q.options ?? [];
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <Field label={q.label}>
        {opts.length === 0 ? (
          <Hint>No options available.</Hint>
        ) : (
          <div className="flex flex-col gap-[6px]" data-testid={`roof-q-${q.id}`}>
            {opts.map((opt) => (
              <label key={opt} className="flex items-center gap-[8px] cursor-pointer">
                <Checkbox
                  checked={selected.includes(opt)}
                  onCheckedChange={(v) =>
                    setAnswer(
                      q.id,
                      v ? [...selected, opt] : selected.filter((o) => o !== opt),
                    )
                  }
                />
                <span className="text-[12.5px] text-[var(--tx-hi)]">{opt}</span>
              </label>
            ))}
          </div>
        )}
        {q.help && <Hint>{q.help}</Hint>}
      </Field>
    );
  }

  // single_select (default)
  return (
    <Field label={q.label}>
      <Select
        data-testid={`roof-q-${q.id}`}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => setAnswer(q.id, e.target.value)}
      >
        {(q.options ?? []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Select>
      {q.help && <Hint>{q.help}</Hint>}
    </Field>
  );
}

/** damage_mode: prominent full_replacement + "Adjust scope" disclosure with a
 * disabled facet_selection (manual entry never has facets) and repair. */
function DamageModeControl({
  q,
  value,
  setAnswer,
  hasFacets,
}: {
  q: RoofQuestion;
  value: unknown;
  setAnswer: (_id: string, _value: unknown) => void;
  hasFacets: boolean;
}) {
  const hints = (q.ui_hints ?? {}) as {
    prominent?: string;
    disclosure?: { label?: string; options?: string[] };
    disabled_options?: Record<string, { enabled_when?: string; disabled_message?: string }>;
    not_supported?: Record<string, string>;
  };
  const prominent = hints.prominent ?? 'full_replacement';
  const disclosureOptions = hints.disclosure?.options ?? [];
  const current = (value as string) ?? prominent;
  const [open, setOpen] = useState(current !== prominent);

  const isDisabled = (opt: string): string | null => {
    const rule = hints.disabled_options?.[opt];
    if (!rule) return null;
    // Only 'measurements.has_facets' appears in the contract; manual entry never
    // has facets, so honor it directly.
    if (rule.enabled_when === 'measurements.has_facets' && !hasFacets) {
      return rule.disabled_message ?? 'Unavailable.';
    }
    return null;
  };

  const optionLabel = (opt: string) =>
    opt.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

  return (
    <Field label={q.label}>
      <div className="flex flex-col gap-[8px]" data-testid={`roof-q-${q.id}`}>
        <button
          type="button"
          data-testid="roof-damage-full_replacement"
          onClick={() => setAnswer(q.id, prominent)}
          className={cn(
            'text-left px-[11px] py-[8px] rounded-[var(--r-md)] border text-[12.5px] font-[530]',
            current === prominent
              ? 'bg-[var(--accent-soft)] border-[var(--accent-line)] text-[var(--tx-hi)]'
              : 'bg-[var(--bg-2)] border-[var(--bd)] text-[var(--tx-mid)] hover:bg-[var(--bg-3)]',
          )}
        >
          {optionLabel(prominent)}
        </button>

        <button
          type="button"
          data-testid="roof-damage-disclosure"
          onClick={() => setOpen((o) => !o)}
          className="self-start text-[11.5px] font-[530] text-[var(--accent-ink)] hover:underline"
        >
          {hints.disclosure?.label ?? 'Adjust scope'}
        </button>

        {open && (
          <div className="flex flex-col gap-[6px] pl-[4px]">
            {disclosureOptions.map((opt) => {
              const disabledMsg = isDisabled(opt);
              const notSupported = hints.not_supported?.[opt];
              return (
                <div key={opt} className="flex flex-col gap-[2px]">
                  <button
                    type="button"
                    data-testid={`roof-damage-${opt}`}
                    disabled={!!disabledMsg}
                    onClick={() => setAnswer(q.id, opt)}
                    className={cn(
                      'text-left px-[11px] py-[7px] rounded-[var(--r-md)] border text-[12.5px] font-[530]',
                      disabledMsg
                        ? 'opacity-50 cursor-not-allowed bg-[var(--bg-2)] border-[var(--bd)] text-[var(--tx-faint)]'
                        : current === opt
                          ? 'bg-[var(--accent-soft)] border-[var(--accent-line)] text-[var(--tx-hi)]'
                          : 'bg-[var(--bg-2)] border-[var(--bd)] text-[var(--tx-mid)] hover:bg-[var(--bg-3)]',
                    )}
                  >
                    {optionLabel(opt)}
                  </button>
                  {disabledMsg && (
                    <span className="text-[11px] text-[var(--tx-faint)] pl-[2px]">{disabledMsg}</span>
                  )}
                  {!disabledMsg && notSupported && current === opt && (
                    <span className="text-[11px] text-[var(--tx-faint)] pl-[2px]">{notSupported}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Field>
  );
}
